// @mui material components
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import LoadingScreen from "components/Loading";
import { useEffect, useState, useRef } from "react";
import AddKpiAssignmentDialog from "components/AddKpiAssignmentDialog";
import { useAuth } from "context/authContext";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CompanyModel from "models/companyModel";
import JobModel from "models/jobModel";
import {
  getJobs,
  getCompanies,
  getKpiAssignments,
  getKpiNames,
  getEvidenceProvidersByKpiAssignment,
  removeEvidenceProvidersByKpiId,
} from "services/api";
import KpiAssignmentsModel from "models/kpiAssignment";
import KpiNamesModel from "models/KpiNamesModel";
import MDButton from "components/MDButton";
import { getEmployees, resetKpiAssignments } from "services/api";
import { saveKpiAssignment, getAllEvidenceProviders } from "services/api";
import {
  Alert,
  Checkbox,
  CircularProgress,
  Fab,
  Icon,
  IconButton,
  Snackbar,
  Stack,
} from "@mui/material";
import { deleteKpiAssignment } from "services/api";
import { saveAllKpiAssignmentChanges } from "services/api";
import ScorecardPlaceholder from "components/ScorecardPlaceholder";
import { getBranches } from "services/api";
import BranchModel from "models/branchModel";
import { getJobToBranch } from "services/api";
import UnpublishConfirmationDialog from "components/UnpublishDialog";
import { CloudDownload, CloudUpload } from "@mui/icons-material";
import { exportData } from "services/api";
import { useNavigate } from "react-router-dom";
import { detectDataType } from "services/utils";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";
import SnackbarWrapper from "components/Snackbar";
import { assignAllFunctionals, assignAllEvidenceProviders } from "services/api";
import { removeFunctionalsByKpiId } from "services/api";
import { getFunctionalAssessorsByKpi } from "services/api";
import ExportMenu from "./components/exportMenu";
import PropTypes from "prop-types";
import { getAllEvidenceProvidersWithUid } from "services/api";
import ConfirmationDialog from "components/ConfirmationDialog";

function ScorecardsPage({ canEdit }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resolveConfirm, setResolveConfirm] = useState(null);
  const [toDeleteKpis, setToDeleteKpis] = useState([]);
  const [state, setState] = useState({
    loading: true,
    kpiAssignments: [],
    kpiNames: [],
    filteredJobs: [],
    selectedBranch: null,
    selectedJob: null,
    filteredKpis: [],
    branches: [],
    openDialog: false,
    newKpi: {
      kpiNameId: null,
      kpiCode: "",
      primaryAssessorUId: null,
      secondaryAssessorUId: null,
      weight: null,
      frequency: "",
      target: null,
      perspectiveId: null,
      source: "",
      from: new Date(),
      to: null,
      primaryPercentage: 100,
      description: "",
    },
    isEditMode: false,
    selectedKpiId: null,
    deleteMode: false,
  });
  const [downloadLoading, setDownloadLoading] = useState(false);
  const { allEmps, setAllEmps, jobs, setJobs, jobToBranch, setJobToBranch, companies } = useAuth();
  const [functionalAssessors, setFunctionalAssessors] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [evidenceProviders, setEvidenceProviders] = useState([]);
  const [evidenceProvidersMap, setEvidenceProvidersMap] = useState({});
  const snackbarRef = useRef();
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };
  const [resetConfOpen, setResetConfOpen] = useState(false);
  const [loadingReset, setLoadingReset] = useState(false);
  // const perspectives = [
  //   "Strategic Initiatives",
  //   "Strategic Projects",
  //   "Finance Viability",
  //   "Internal Process",
  //   "Internal StakeHolder",
  //   "External StakeHolder",
  //   "Compliance",
  //   "Audit",
  // ];

  const perspectives = {
    1: "strategicInit",
    2: "strategicProjects",
    3: "financeViability",
    4: "internalProcess",
    5: "internalStakeholder",
    6: "externalStakeholder",
    7: "compliance",
    8: "audit",
  };

  useEffect(() => {
    const fetchData = async () => {
      setState((prev) => ({ ...prev, loading: true }));

      try {
        const fetchJobs = jobs?.length ? Promise.resolve("cached") : getJobs();
        const fetchEmployees = allEmps?.length ? Promise.resolve("cached") : getEmployees();
        const fetchBranches = getBranches(); // assuming you always fetch fresh branches
        const fetchJobToBranch = jobToBranch?.length ? Promise.resolve("cached") : getJobToBranch();
        const fetchKpiAssignments = getKpiAssignments();
        const fetchKpiNames = getKpiNames();
        // const fetchEvidenceProviders = getAllEvidenceProviders();
        const fetchEvidenceProviders = getAllEvidenceProvidersWithUid();

        const [js, emps, br, jtb, kpiAssignments, kpiNamesData, evidProviders] = await Promise.all([
          fetchJobs,
          fetchEmployees,
          fetchBranches,
          fetchJobToBranch,
          fetchKpiAssignments,
          fetchKpiNames,
          fetchEvidenceProviders,
        ]);

        if (emps !== "error" && emps !== "cached") {
          setAllEmps(emps);
        }

        if (js !== "error" && js !== "cached") {
          setJobs(js.map((j) => new JobModel(j)));
        }

        if (jtb !== "error" && jtb !== "cached") {
          setJobToBranch(jtb);
        }

        if (evidProviders !== "error") {
          const map = {};

          evidProviders.forEach((item) => {
            const kpiId = item[1];
            const empId = item[2];

            if (!map[kpiId]) {
              map[kpiId] = [];
            }

            map[kpiId].push(empId);
          });

          setEvidenceProvidersMap(map);
        }

        setState((prev) => ({
          ...prev,
          branches: br !== "error" ? br.map((b) => new BranchModel(b)) : [],
          kpiAssignments:
            kpiAssignments !== "error" ? kpiAssignments.map((e) => new KpiAssignmentsModel(e)) : [],
          kpiNames: kpiNamesData !== "error" ? kpiNamesData.map((k) => new KpiNamesModel(k)) : [],
        }));
      } catch (error) {
      } finally {
        setState((prev) => ({ ...prev, loading: false }));
      }
    };

    fetchData();
  }, []);

  const loadData = async () => {
    const kpis = await getKpiAssignments();
    if (kpis !== "error") {
      const kpismapped = kpis.map((e) => new KpiAssignmentsModel(e));
      setState((prev) => ({
        ...prev,
        kpiAssignments: kpismapped,
      }));
      return kpismapped;
    }
  };

  const updateState = (updates) => {
    setState((prevState) => ({ ...prevState, ...updates }));
  };

  // Handle selecting/deselecting a KPI
  const handleSelectKpi = (kpiId) => {
    setToDeleteKpis((prev) =>
      prev.includes(kpiId) ? prev.filter((id) => id !== kpiId) : [...prev, kpiId]
    );
  };

  const handleBranchChange = (event, newValue) => {
    if (!newValue) {
      updateState({
        selectedBranch: null,
        selectedJob: null,
        filteredKpis: [],
        filteredJobs: [],
      });
      return;
    }

    // The job/branch links and jobs may not have loaded (or their request failed): treat them as empty, don't crash.
    const jobIdsForBranch = (jobToBranch || [])
      .filter((entry) => entry.branchId === newValue.branchId)
      .map((entry) => entry.jobId);

    updateState({
      selectedBranch: newValue,
      selectedJob: null,
      filteredKpis: [],
      filteredJobs: (jobs || []).filter((job) => jobIdsForBranch.includes(job.jobId)),
    });
  };

  const handleJobChange = (event, newValue) => {
    if (!newValue) {
      updateState({ selectedJob: null, filteredKpis: [], deleteMode: false });
      setToDeleteKpis([]);
      return;
    }

    const today = new Date();

    // Group KPIs by kpiNameId and pick the latest version that is still valid
    const latestJobKpis = state.kpiAssignments
      .filter(
        (kpi) =>
          kpi.jobId === newValue.jobId &&
          kpi.branchId === state.selectedBranch?.branchId &&
          kpi.from <= today && // Ensure it's valid for today
          (kpi.to === null || kpi.to >= today) // Ensure it's still active
      )
      .reduce((latestKpis, kpi) => {
        const existingKpi = latestKpis[kpi.kpiNameId];

        // Keep the KPI with the most recent 'from' date
        if (!existingKpi || new Date(kpi.from) > new Date(existingKpi.from)) {
          latestKpis[kpi.kpiNameId] = kpi;
        }

        return latestKpis;
      }, {});

    // Convert back to an array
    const jobKpis = Object.values(latestJobKpis);
    setToDeleteKpis([]);
    updateState({ selectedJob: newValue, filteredKpis: jobKpis, deleteMode: false });
  };

  const filterKpis = (kpis) => {
    if (!state.selectedJob) {
      updateState({ selectedJob: null, filteredKpis: [] });
      return;
    }

    const today = new Date();

    // Group KPIs by kpiNameId and pick the latest version that is still valid
    const latestJobKpis = kpis
      .filter(
        (kpi) =>
          kpi.jobId === state.selectedJob.jobId &&
          kpi.branchId === state.selectedBranch?.branchId &&
          kpi.from <= today && // Ensure it's valid for today
          (kpi.to === null || kpi.to >= today) // Ensure it's still active
      )
      .reduce((latestKpis, kpi) => {
        const existingKpi = latestKpis[kpi.kpiNameId];

        // Keep the KPI with the most recent 'from' date
        if (!existingKpi || new Date(kpi.from) > new Date(existingKpi.from)) {
          latestKpis[kpi.kpiNameId] = kpi;
        }

        return latestKpis;
      }, {});

    // Convert back to an array
    const jobKpis = Object.values(latestJobKpis);

    updateState({ filteredKpis: jobKpis });
  };

  const handlePublish = async () => {
    updateState({ loading: true });

    const totalWeight = state.filteredKpis.reduce((sum, kpi) => sum + (kpi.weight || 0), 0);

    if (totalWeight !== 100) {
      alert("Total KPI weight must be 100% before publishing.");
      updateState({ loading: false });
      return;
    }

    const updatedKpis = state.filteredKpis.map((kpi) => ({
      ...kpi,
      publish: true,
    }));

    updateState({ filteredKpis: updatedKpis });

    try {
      const response = await saveAllKpiAssignmentChanges(updatedKpis);
      if (response !== "error") {
        await loadData();
      }
    } catch (error) {
    } finally {
      updateState({ loading: false });
    }
  };

  const handleUnPublish = async () => {
    updateState({ loading: true });

    const updatedKpis = state.filteredKpis.map((kpi) => ({
      ...kpi,
      publish: false,
    }));

    updateState({ filteredKpis: updatedKpis });

    try {
      const response = await saveAllKpiAssignmentChanges(updatedKpis);
      if (response !== "error") {
        await loadData();
      }
    } catch (error) {
      console.error("Error unpublishing KPIs:", error);
    } finally {
      updateState({ loading: false });
    }
  };

  const handleAddKpi = () => {
    updateState({ openDialog: true });
  };

  //Add KPI To Scorecard
  const handleSaveKpi = async () => {
    updateState({ loading: true });
    const now = new Date();
    const yesterday = new Date();
    const { selectedJob, selectedBranch, newKpi, filteredKpis } = state;
    const oldToNewMap = new Map();

    const kpiToSave = {
      ...newKpi,
      jobId: selectedJob?.jobId,
      branchId: selectedBranch?.branchId,
      publish: false, // New KPI is hidden until admin publishes
      from: new Date(), // Starts today
      to: null, // Active
    };

    try {
      // Is there already a kpi Assignment with this kpi Name Id (for this kpi at the job) ?
      const existingKpi = filteredKpis.find(
        (kpi) => kpi.kpiNameId === newKpi.kpiNameId && kpi.to == null
      );

      // If same kpi Name Id found
      if (existingKpi) {
        // Only update if it was published (avoid redundant updates)
        if (existingKpi.publish) {
          // mark the assignment as ended
          const updatedOldKpi = { ...existingKpi, to: new Date() }; // Ended yesterday
          await saveKpiAssignment(updatedOldKpi);
        }
      }

      // Check if all kpi assignments are published in this job
      const allPublished = filteredKpis.every((kpi) => kpi.publish);

      if (allPublished) {
        //Mark all old KPI assignments as finished
        const oldKpis = filteredKpis.map((kpi) => ({
          ...kpi,
          to: yesterday, // End the old KPIs
        }));

        // Create a new unpublished copy for all KPIs, including the new one**
        const newKpis = filteredKpis.map((kpi) => ({
          ...kpi,
          kpiAssignmentsId: null, // New version
          from: now, // Starts today
          to: null, // Active
          publish: false, // Unpublished until explicitly published
        }));

        const newKpiIndex = newKpis.length;

        // Add the new KPI as part of the new batch
        newKpis.push({ ...kpiToSave, kpiAssignmentsId: null, from: now, to: null, publish: false });

        //Save all KPI Assignment changes
        const savedAssignments = await saveAllKpiAssignmentChanges([...oldKpis, ...newKpis]);

        //Loop over old kpis and find the matching new version
        filteredKpis.forEach((oldKpi) => {
          const newKpi = savedAssignments.find(
            (k) =>
              k.kpiNameId === oldKpi.kpiNameId &&
              k.jobId === oldKpi.jobId &&
              k.branchId === oldKpi.branchId &&
              k.from &&
              new Date(k.from).toDateString() === now.toDateString()
          );

          // Map between old and new kpi assignment Ids
          if (newKpi) {
            oldToNewMap.set(oldKpi.kpiAssignmentsId, newKpi.kpiAssignmentsId);
          }
        });

        // Loops over evert old → new mapping.
        for (const [oldId, newId] of oldToNewMap.entries()) {
          // Functional Assessors

          await removeFunctionalsByKpiId(oldId);
          const oldAssessors = await getFunctionalAssessorsByKpi(oldId);
          if (oldAssessors.length > 0) {
            const ids = oldAssessors.map((e) => e.employeeId);
            await assignAllFunctionals(newId, ids);
            setFunctionalAssessors([]);
          }
          //Functional Assessors New

          //get evidence providers by kpi ass id (for old providers)
          const oldEvidenceProviders = await getEvidenceProvidersByKpiAssignment(oldId);
          //deletes all evidence provider records linked to the old kpi ass
          await removeEvidenceProvidersByKpiId(oldId);

          // Only continue if there were providers.
          if (oldEvidenceProviders.length > 0) {
            // extract the employee ids of the old evidence providers
            const ids = oldEvidenceProviders.map((e) => e.employeeUId);
            // await assignAllEvidenceProviders(new Id, ids);
            await assignAllEvidenceProviders(newId, ids);
            // ✅ Update map so UI sees them
            setEvidenceProvidersMap((prev) => ({
              ...prev,
              [newId]: ids, // only UUIDs now
            }));
          }
        }
        const newCreatedKpi = savedAssignments[oldKpis.length + newKpiIndex]; //gets the new record

        if (newCreatedKpi) {
          const newId = newCreatedKpi.kpiAssignmentsId;

          // 🔹 Grab the employees selected BEFORE ID changed
          const selectedEvidence = evidenceProvidersMap[state.selectedKpiId] || [];

          if (selectedEvidence.length > 0) {
            await assignAllEvidenceProviders(newId, selectedEvidence);

            // Update map so UI shows the employees instantly
            setEvidenceProvidersMap((prev) => ({
              ...prev,
              [newId]: selectedEvidence,
            }));
          }
          // 🔹 Now switch the selected KPI
          updateState({ selectedKpiId: newId });
        }
      } else {
        //If an unpublished KPI already exists, just add the new one**
        const newKpiAssignment = new KpiAssignmentsModel(kpiToSave);
        const result = await saveKpiAssignment(newKpiAssignment);

        if (result !== "error") {
          const newId = result.kpiAssignmentsId;
          if (functionalAssessors.length > 0) {
            await assignAllFunctionals(result.kpiAssignmentsId, functionalAssessors);
            setFunctionalAssessors([]);
          }
          const selectedEvidence = evidenceProvidersMap[state.selectedKpiId] || [];
          if (selectedEvidence.length > 0) {
            await assignAllEvidenceProviders(newId, selectedEvidence);
            setEvidenceProvidersMap((prev) => ({
              ...prev,
              [newId]: selectedEvidence,
            }));
          }
        }
      }

      loadData().then((e) => {
        if (e) filterKpis(e);
        updateState({ loading: false });
      });

      // **Reset Form State**
      updateState({
        openDialog: false,
        newKpi: {
          kpiNameId: null,
          kpiCode: "",
          primaryAssessorUId: null,
          weight: null,
          frequency: "",
          target: null,
          perspectiveId: null,
          source: "",
          from: new Date(),
          to: null,
          accountability: "",
          description: "",
        },
      });
    } catch (error) {
      console.error("Error saving KPI:", error);
      updateState({ loading: false });
    }
  };

  // Open dialog in edit mode with KPI data
  const handleEditKpi = (kpi) => {
    if (!canEdit) return;
    updateState({
      newKpi: {
        kpiNameId: kpi.kpiNameId,
        kpiCode: kpi.kpiCode,
        primaryAssessorUId: kpi.primaryAssessorUId,
        weight: kpi.weight,
        frequency: kpi.frequency,
        target: kpi.target,
        perspectiveId: kpi.perspectiveId,
        source: kpi.source,
        from: kpi.from,
        to: kpi.to,
        accountability: kpi.accountability,
        primaryPercentage: kpi.primaryPercentage,
        description: kpi.description,
        secondaryAssessorUId: kpi.secondaryAssessorUId,
      },
      selectedKpiId: kpi.kpiAssignmentsId,
      isEditMode: true,
      openDialog: true,
    });
  };

  // Handle saving edited KPI
  const handleSaveEditedKpi = async () => {
    updateState({ loading: true });

    const yesterday = new Date();
    const now = new Date();
    const { selectedKpiId, filteredKpis, newKpi, selectedJob, selectedBranch } = state;
    const oldToNewMap = new Map();

    // Find the existing KPI
    const oldKpi = filteredKpis.find((kpi) => kpi.kpiAssignmentsId === selectedKpiId);
    if (!oldKpi) {
      updateState({ loading: false });
      return;
    }

    try {
      // **Check if all KPIs are published**
      const allPublished = filteredKpis.every((kpi) => kpi.publish);

      // **Check total weight of remaining KPIs**

      if (allPublished) {
        // **Mark all old KPIs as finished**
        const oldKpis = filteredKpis.map((kpi) => ({
          ...kpi,
          to: yesterday, // End the old KPIs
        }));

        // **Create a new unpublished copy for all KPIs**
        const newKpis = filteredKpis.map((kpi) => ({
          ...(kpi.kpiAssignmentsId === selectedKpiId ? { ...oldKpi, ...newKpi } : kpi),
          kpiAssignmentsId: null, // New version
          from: now, // Starts today
          to: null, // Active
          publish: false, // Unpublished until explicitly published
        }));
        // **Save all KPI changes**
        const savedAssignments = await saveAllKpiAssignmentChanges([...oldKpis, ...newKpis]);

        filteredKpis.forEach((oldKpi) => {
          const newKpi = savedAssignments.find(
            (k) =>
              k.kpiNameId === oldKpi.kpiNameId &&
              k.jobId === oldKpi.jobId &&
              k.branchId === oldKpi.branchId &&
              k.to == null
          );
          if (newKpi) {
            oldToNewMap.set(oldKpi.kpiAssignmentsId, newKpi.kpiAssignmentsId);
          }
        });
        for (const [oldId, newId] of oldToNewMap.entries()) {
          if (oldId === selectedKpiId) {
            // This is the one the user edited – use the new selected assessors
            if (functionalAssessors.length > 0) {
              await assignAllFunctionals(newId, functionalAssessors);
              setFunctionalAssessors([]);
            }
            const selectedEvidence = evidenceProvidersMap[oldId] || [];
            if (selectedEvidence.length > 0) {
              await assignAllEvidenceProviders(newId, selectedEvidence);
              setEvidenceProvidersMap((prev) => ({ ...prev, [newId]: selectedEvidence }));
            }
          } else {
            // Other KPIs – clone their existing assessors
            const oldAssessors = await getFunctionalAssessorsByKpi(oldId);
            const ids = oldAssessors.map((e) => e.employeeId);
            if (oldAssessors.length > 0) {
              await assignAllFunctionals(newId, ids);
              setFunctionalAssessors([]);
            }
            const oldEvidenceProviders = await getEvidenceProvidersByKpiAssignment(oldId);
            const evIds = oldEvidenceProviders.map((e) => e.employeeUId);
            if (evIds.length > 0) {
              await assignAllEvidenceProviders(newId, evIds);
              setEvidenceProvidersMap((prev) => ({ ...prev, [newId]: evIds }));
            }
          }
        }
      } else {
        // **Just update the selected KPI if there are unpublished ones**
        const updatedKpi = { ...oldKpi, ...newKpi };
        const result = await saveKpiAssignment(updatedKpi);

        if (result !== "error") {
          await removeFunctionalsByKpiId(result.kpiAssignmentsId);

          if (functionalAssessors.length > 0) {
            await assignAllFunctionals(result.kpiAssignmentsId, functionalAssessors);
            setFunctionalAssessors([]);
          } else {
          }
          await removeEvidenceProvidersByKpiId(result.kpiAssignmentsId);
          const selectedEvidence = evidenceProvidersMap[state.selectedKpiId] || [];
          if (selectedEvidence.length > 0) {
            await assignAllEvidenceProviders(result.kpiAssignmentsId, selectedEvidence);
            // Optional: update map so UI shows them
            setEvidenceProvidersMap((prev) => ({
              ...prev,
              [result.kpiAssignmentsId]: selectedEvidence,
            }));
          }
        }
      }

      // **Update frontend state & reload data**
      loadData().then((e) => {
        if (e) filterKpis(e);
        updateState({ loading: false });
      });

      // **Reset Form State**
      updateState({
        openDialog: false,
        isEditMode: false,
        newKpi: {
          kpiNameId: null,
          kpiCode: "",
          primaryAssessorUId: null,
          weight: null,
          frequency: "",
          target: null,
          perspectiveId: null,
          source: "",
          from: new Date(),
          to: null,
          primaryPercentage: 100,
          description: "",
        },
      });
    } catch (error) {
      console.error("Error editing KPI:", error);
      updateState({ loading: false });
    }
  };

  const handleResetPublishedKpis = async () => {
    if (!state.selectedJob) return false;

    updateState({ loading: true });

    try {
      console.log(
        "Resetting KPIs for Job ID:",
        state.selectedJob.jobId,
        "Branch ID:",
        state.selectedBranch.branchId
      );
      const response = await resetKpiAssignments(
        state.selectedJob.jobId,
        state.selectedBranch.branchId
      );
      console.log("Server Response:", response);
      if (response && response.status === 200) {
        const e = await loadData();
        if (e) filterKpis(e);
        updateState({ loading: false });
        return true; // SUCCESS
      } else {
        updateState({ loading: false });
        return false;
      }
    } catch (error) {
      updateState({ loading: false });
      return false;
    }
  };

  const handleDeleteKpi = async (kpiId) => {
    // const confirm = await handleConfirmUnpublish();
    // if (!confirm) {
    //   updateState({ loading: false });
    //   return; // **Stop execution if canceled**
    // }
    updateState({ loading: true });

    const { filteredKpis } = state;
    const kpiToDelete = filteredKpis.find((kpi) => kpi.kpiAssignmentsId === kpiId);

    if (!kpiToDelete) {
      updateState({ loading: false });
      return;
    }

    try {
      let remainingKpis = filteredKpis.filter((kpi) => kpi.kpiAssignmentsId !== kpiId);

      if (!kpiToDelete.publish) {
        const res = await deleteKpiAssignment(kpiId);
        if (res === "error") throw new Error("Failed to delete KPI");
      }

      // **Check if all remaining KPIs are unpublished**
      const allUnpublished = remainingKpis.every((kpi) => !kpi.publish);

      // **Check total weight of remaining KPIs**
      if (allUnpublished) {
        // **Update existing unpublished KPIs**
        const updatedKpis = remainingKpis.map((kpi) => ({
          ...kpi,
          publish: false, // Keep them unpublished
        }));

        await saveAllKpiAssignmentChanges(updatedKpis);
        remainingKpis = updatedKpis;
      } else {
        // **Create new unpublished versions**
        const newKpis = remainingKpis.map((kpi) => ({
          ...kpi,
          from: new Date(), // Start from today
          to: null, // Reset 'to' for the new version
          publish: false, // Unpublished but still exists in history
          kpiAssignmentsId: undefined,
        }));

        const oldKpis = filteredKpis.map((kpi) => ({
          ...kpi,
          to: new Date(), // Reset 'to' for the new version
        }));

        await saveAllKpiAssignmentChanges([...oldKpis, ...newKpis]);
        remainingKpis = newKpis;
      }

      loadData().then((e) => {
        if (e) filterKpis(e);
        updateState({ loading: false });
      });
      // Update state
    } catch (error) {
      console.error("Error deleting KPI:", error);
      updateState({ loading: false });
    }

    if (state.openDialog) {
      updateState({ openDialog: false });
    }
  };

  const handleCopyScorecard = async (jobId, branchId) => {
    updateState({ loading: true });

    const today = new Date();
    const kpisToCopy = state.kpiAssignments.filter(
      (kpi) =>
        kpi.jobId === jobId &&
        kpi.branchId === branchId &&
        kpi.publish === true &&
        kpi.from <= today &&
        (kpi.to === null || kpi.to >= today)
    );

    const copiedKPIs = kpisToCopy.map((kpi) => ({
      ...kpi,
      jobId: state.selectedJob?.jobId, // Assign new jobId
      branchId: state.selectedBranch?.branchId,
      from: today, // Set new start date
      kpiAssignmentsId: null,
      publish: false,
    }));

    await saveAllKpiAssignmentChanges(copiedKPIs);

    loadData().then((kpisMapped) => {
      const filt = kpisMapped.filter(
        (kpi) =>
          kpi.jobId === state.selectedJob?.jobId &&
          kpi.branchId === state.selectedBranch?.branchId &&
          kpi.to == null
      );

      updateState({ filteredKpis: filt, loading: false });
    });
  };

  const getJobsWithScorecards = () => {
    const jobIdsWithKPIs = new Set(state.kpiAssignments?.map((kpi) => kpi.jobId));
    return jobs.filter((job) => jobIdsWithKPIs.has(job.jobId));
  };

  const restoreLastPublished = async () => {
    updateState({ loading: true });

    try {
      const { filteredKpis, kpiAssignments } = state;

      // Get all unpublished KPIs (those without publish)
      const unpublishedKpis = filteredKpis.filter((kpi) => !kpi.publish);

      // Find all past published versions (to !== null)
      const publishedHistory = kpiAssignments.filter(
        (kpi) => kpi.publish && kpi.to !== null && kpi.jobId == state.selectedJob.jobId
      );

      // Group all published KPIs by their `to` date (to find full versions)
      const groupedByToDate = publishedHistory.reduce((acc, kpi) => {
        const key = kpi.to; // Grouping by end date
        if (!acc[key]) acc[key] = [];
        acc[key].push(kpi);
        return acc;
      }, {});

      // Find the last full published version where totalWeight = 100%
      let lastCompletePublishedKpis = [];
      for (const [_, kpis] of Object.entries(groupedByToDate).sort(
        (a, b) => new Date(b[0]) - new Date(a[0])
      )) {
        lastCompletePublishedKpis = kpis;
      }
      if (lastCompletePublishedKpis.length === 0) {
        updateState({ loading: false });
        return;
      }
      // Delete all unpublished KPIs
      await Promise.all(unpublishedKpis?.map((kpi) => deleteKpiAssignment(kpi.kpiAssignmentsId)));

      // Prepare updated KPIs (restoring last published version)
      const updatedKpis = lastCompletePublishedKpis.map((kpi) => ({
        ...kpi,
        to: null, // Make it the active version
      }));
      // Save all restored KPI assignments
      await saveAllKpiAssignmentChanges(updatedKpis);

      loadData().then((e) => {
        if (e) filterKpis(e);
        updateState({ loading: false });
      });
    } catch (error) {
      updateState({ loading: false });
    }
  };

  if (state.loading) {
    return <LoadingScreen />;
  }

  const handleConfirmUnpublish = () => {
    setConfirmOpen(true);

    return new Promise((resolve) => {
      setResolveConfirm(() => resolve); // Store resolve function
    });
  };

  const handleDeleteSelectedKpis = async () => {
    updateState({ loading: true });

    const { filteredKpis } = state;
    const selectedKpis = filteredKpis.filter((kpi) => toDeleteKpis.includes(kpi.kpiAssignmentsId));

    if (selectedKpis.length === 0) {
      updateState({ loading: false });
      return;
    }

    try {
      const allUnpublished = selectedKpis.every((kpi) => !kpi.publish); // Check if all KPIs are unpublished
      let remainingKpis = filteredKpis.filter(
        (kpi) => !toDeleteKpis.includes(kpi.kpiAssignmentsId)
      ); // Exclude deleted ones

      if (allUnpublished) {
        // **All KPIs are unpublished → Delete permanently**
        await Promise.all(selectedKpis?.map((kpi) => deleteKpiAssignment(kpi.kpiAssignmentsId)));
      } else {
        // **Some or all KPIs are published**
        const newKpis = remainingKpis.map((kpi) => ({
          ...kpi,
          from: new Date(), // Start from today
          to: null, // Reset 'to' for the new version
          publish: false, // Unpublished but still exists in history
          kpiAssignmentsId: undefined, // New record
        }));

        const oldKpis = filteredKpis.map((kpi) => ({
          ...kpi,
          to: new Date(), // Set `to` as yesterday
        }));

        await saveAllKpiAssignmentChanges([...oldKpis, ...newKpis]);
      }

      // Reload KPI list after update
      loadData().then((e) => {
        if (e) filterKpis(e);
        setToDeleteKpis([]);
        updateState({ loading: false, deleteMode: false });
      });

      // Close dialog if it's open
      if (state.openDialog) {
        updateState({ openDialog: false });
      }
    } catch (error) {
      console.error("Error deleting KPI:", error);
      setToDeleteKpis([]);
      updateState({ loading: false, deleteMode: false });
    }
  };

  const handleExport = async (type) => {
    setDownloadLoading(true);
    await exportData(type);
    setDownloadLoading(false);
  };

  // Group KPIs by perspective
  const kpisByPerspective = {};
  let totalWeight = 0;

  state.filteredKpis.forEach((kpi) => {
    const kpiDetails = state.kpiNames.find((kn) => kn.kpiNameId === kpi.kpiNameId);
    const perspectiveid = kpiDetails?.perspectiveId || "Uncategorized";
    // const perspective = perspectives[perspectiveid - 1];
    // if (!kpisByPerspective[perspective]) {
    //   kpisByPerspective[perspective] = { kpis: [], totalWeight: 0 };
    // }
    // kpisByPerspective[perspective].kpis.push({
    //   ...kpi,
    //   kpiName: kpiDetails?.kpiName || "Unknown KPI",
    //   units: kpiDetails?.units,
    // });
    // kpisByPerspective[perspective].totalWeight += kpi.weight || 0;
    // NEW PART////
    const perspective = perspectives[perspectiveid];
    const perspectiveLabel = perspective ? t(`admin.perspectives.${perspective}`) : "Uncategorized";

    if (!kpisByPerspective[perspectiveLabel]) {
      kpisByPerspective[perspectiveLabel] = { kpis: [], totalWeight: 0 };
    }
    kpisByPerspective[perspectiveLabel].kpis.push({
      ...kpi,
      kpiName: kpiDetails?.kpiName || "Unknown KPI",
      units: kpiDetails?.units,
    });
    kpisByPerspective[perspectiveLabel].totalWeight += kpi.weight || 0;
    // NEW PART////
    totalWeight += kpi.weight || 0;
  });

  const handleImportError = (message, severity) => {
    snackbarRef.current?.showSnackbar(message, severity);
  };

  return (
    <MDBox mt={8}>
      {/* Import / Export Buttons */}
      <Grid container justifyContent="flex-end" mb={2}>
        <Grid item>
          <ExportMenu
            handleExport={handleExport}
            handleImportError={handleImportError}
            downloadLoading={downloadLoading}
            canEdit={canEdit}
            companyId={companies ? companies[0].companyId : null}
          />
        </Grid>
      </Grid>

      {/* Filters: Branch and Job */}
      <Grid container spacing={2} mb={3}>
        {/* Searchable Company Filter */}
        <Grid item xs={12} md={6}>
          <Autocomplete
            options={state.branches}
            getOptionLabel={(option) => option.branchName}
            value={state.selectedBranch}
            onChange={handleBranchChange}
            isOptionEqualToValue={(option, value) => option.branchName === value.branchName}
            renderInput={(params) => (
              <TextField {...params} label={t("admin.scorecard.selectBranch")} fullWidth />
            )}
          />
        </Grid>

        {/* Searchable Job Filter */}
        <Grid item xs={12} md={6}>
          <Autocomplete
            options={state.filteredJobs}
            getOptionLabel={(option) => option.jobName}
            value={state.selectedJob}
            onChange={handleJobChange}
            renderInput={(params) => (
              <TextField {...params} label={t("admin.scorecard.selectJob")} fullWidth />
            )}
          />
        </Grid>
      </Grid>

      <MDBox mt={4}>
        {state.selectedJob && (
          <Grid container alignItems="center" justifyContent="space-between">
            {/* Left Side: KPI Assignments Title */}
            <MDBox display="flex" flexDirection="column">
              <MDTypography variant="h5" mb={1}>
                {t("admin.scorecard.detailsFor")}{" "}
                {state.selectedJob?.jobName || t("admin.scorecard.noJobSelected")}
              </MDTypography>

              {/* Buttons Side by Side */}
              <MDBox display="flex" gap={1} mb={1}>
                {state.filteredKpis.length > 1 && (
                  <MDButton
                    variant="outlined"
                    disabled={!canEdit}
                    color={state.deleteMode ? "secondary" : "error"}
                    onClick={() => {
                      updateState({ deleteMode: !state.deleteMode });
                    }}
                  >
                    {state.deleteMode
                      ? t("admin.scorecard.cancel")
                      : t("admin.scorecard.deleteMultiple")}
                  </MDButton>
                )}
              </MDBox>
              {state.deleteMode && toDeleteKpis.length > 0 && (
                <MDButton
                  variant="contained"
                  color="error"
                  disabled={!canEdit}
                  sx={{ marginBottom: 1 }}
                  onClick={handleDeleteSelectedKpis}
                >
                  {t("admin.scorecard.confirmDelete")} ({toDeleteKpis.length})
                </MDButton>
              )}
            </MDBox>

            {/* Right Side: Publish Status & Total Weight */}
            <MDBox display="flex" flexDirection="column" alignItems="flex-end">
              <MDBox display="flex" flexDirection="row" alignItems="center" gap={2}>
                {/* Reset Published KPIs */}
                <MDButton
                  variant="outlined"
                  color="warning"
                  onClick={() => setResetConfOpen(true)}
                  disabled={!canEdit}
                >
                  {t("admin.scorecard.resetKPIs")}
                </MDButton>
                {state.filteredKpis.length > 0 && state.filteredKpis.every((kpi) => kpi.publish) ? (
                  <MDTypography variant="h6" color="success">
                    ✅ {t("admin.scorecard.kpisPublished")}
                  </MDTypography>
                ) : (
                  <MDButton
                    variant="contained"
                    color="primary"
                    onClick={handlePublish}
                    disabled={totalWeight !== 100 || !canEdit}
                  >
                    {t("admin.scorecard.publishKpis")}
                  </MDButton>
                )}
              </MDBox>
              {/* Total Weight Below Publish Button - now aligned right */}
              <MDTypography variant="h6" color="info" mt={1} textAlign="right">
                {t("admin.scorecard.totalWeight")}: {totalWeight}%
              </MDTypography>
            </MDBox>
          </Grid>
        )}

        {/* KPI Data Section (Only render if KPIs exist) */}
        {Object.keys(kpisByPerspective).length > 0 ? (
          Object.entries(kpisByPerspective)?.map(
            ([perspective, { kpis, totalWeight: perspectiveWeight }]) => (
              <MDBox key={perspective} mb={4} p={2} border="1px solid #ddd" borderRadius="8px">
                <Grid container alignItems="center" justifyContent="space-between">
                  <MDTypography variant="h6" color="info" mb={2}>
                    {perspective}
                  </MDTypography>
                  <MDTypography variant="h6" color="info" mb={2}>
                    {t("admin.scorecard.perspectiveWeight")}: {perspectiveWeight}%
                  </MDTypography>
                </Grid>
                <Grid container spacing={2}>
                  {kpis?.map((kpi) => (
                    <Grid item xs={12} md={6} lg={4} key={kpi.kpiAssignmentsId}>
                      <Card
                        onClick={() => handleEditKpi(kpi)}
                        sx={{
                          cursor: "pointer",
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                        }}
                      >
                        <CardContent
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            flexGrow: 1,
                            minHeight: 120,
                          }}
                        >
                          <MDBox>
                            <MDTypography
                              variant="h6"
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "100%",
                              }}
                            >
                              {kpi.kpiName}
                            </MDTypography>
                          </MDBox>

                          <MDBox sx={{ marginTop: "auto" }}>
                            <MDTypography variant="body2">
                              {t("admin.scorecard.weight")}: {kpi.weight}% |{" "}
                              {t("admin.scorecard.target")}: {kpi.target}
                            </MDTypography>
                            <MDTypography variant="body2">
                              {t("admin.scorecard.frequency")}: {kpi.frequency} |{" "}
                              {t("admin.scorecard.unit")}: {kpi.units}
                            </MDTypography>
                          </MDBox>

                          {state.deleteMode ? (
                            <Checkbox
                              checked={toDeleteKpis.includes(kpi.kpiAssignmentsId)}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              onChange={() => handleSelectKpi(kpi.kpiAssignmentsId)}
                              sx={{
                                position: "absolute",
                                right: 16,
                                top: "50%",
                                transform: "translateY(-50%)",
                              }}
                            />
                          ) : (
                            <IconButton
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent card click event
                                handleDeleteKpi(kpi.kpiAssignmentsId);
                              }}
                              sx={{
                                position: "absolute",
                                right: 16,
                                top: "50%",
                                transform: "translateY(-50%)",
                              }}
                              disabled={!canEdit}
                            >
                              <Icon fontSize="small">delete</Icon>
                            </IconButton>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </MDBox>
            )
          )
        ) : state.selectedJob ? (
          <ScorecardPlaceholder
            jobsWithScorecards={getJobsWithScorecards()}
            onCopyScorecard={handleCopyScorecard}
            jobToBranch={jobToBranch}
            branches={state.branches}
            canEdit={canEdit}
          />
        ) : (
          ""
        )}

        {/* Floating Action Button for Add KPI (Always Visible) */}
        {state.selectedJob && canEdit && (
          <MDBox sx={{ position: "fixed", bottom: 20, right: 20 }}>
            <Fab color="primary" aria-label="add" onClick={handleAddKpi}>
              <Icon>add</Icon>
            </Fab>
          </MDBox>
        )}
      </MDBox>

      {state.selectedJob && (
        <AddKpiAssignmentDialog
          open={state.openDialog}
          onClose={() => {
            updateState({
              openDialog: false,
              isEditMode: false,
              newKpi: {
                kpiNameId: null,
                kpiCode: "",
                primaryAssessorUId: null,
                weight: null,
                frequency: "",
                target: null,
                perspectiveId: null,
                source: "",
                from: new Date(),
                to: null,
                primaryPercentage: 100,
                description: "",
                secondaryAssessorUId: null,
              },
            });
          }}
          onSubmit={state.isEditMode ? handleSaveEditedKpi : handleSaveKpi}
          newKpi={state.newKpi}
          setNewKpi={(newKpi) => updateState({ newKpi })}
          kpiNames={state.kpiNames}
          employees={allEmps}
          isEditMode={state.isEditMode}
          filteredKpis={state.filteredKpis}
          setIsEditMode={(isEditMode) => updateState({ isEditMode })}
          setSelectedKpiId={(selectedKpiId) => updateState({ selectedKpiId })}
          onDelete={() => handleDeleteKpi(state.selectedKpiId)}
          functionalAssessors={functionalAssessors}
          setFunctionalAssessors={setFunctionalAssessors}
          selectedKpiId={state.selectedKpiId}
          jobs={jobs}
          evidenceProviders={evidenceProvidersMap[state.selectedKpiId] || []}
          setEvidenceProviders={(ids) =>
            setEvidenceProvidersMap((prev) => ({
              ...prev,
              [state.selectedKpiId]: ids,
            }))
          }
        />
      )}
      <UnpublishConfirmationDialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          if (resolveConfirm) resolveConfirm(false); // Reject promise if canceled
        }}
        onConfirm={() => {
          setConfirmOpen(false);
          if (resolveConfirm) resolveConfirm(true); // Resolve promise if confirmed
        }}
      />

      <ConfirmationDialog
        open={resetConfOpen}
        onClose={() => setResetConfOpen(false)}
        message={t("admin.scorecard.confirmResetKpis")}
        onConfirm={async () => {
          setLoadingReset(true);
          const success = await handleResetPublishedKpis();
          setResetConfOpen(false);
          setTimeout(() => {
            if (!success) {
              console.log("Showing Error Snackbar for 403");
              snackbarRef.current?.showSnackbar(t("admin.scorecard.cannotResetKpis"), "error");
            }
          }, 100);
          setLoadingReset(false);
        }}
      />
      <SnackbarWrapper
        ref={snackbarRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </MDBox>
  );
}

ScorecardsPage.propTypes = {
  canEdit: PropTypes.bool.isRequired,
};

export default ScorecardsPage;
